<template>
  <service-wrapper
    :title="$t('DEALS.CODAT.TITLE')"
    :subtitle="$t('DEALS.DEAL_PROGRESS.UNDERWRITING.ACCOUNTING')"
    :is-success="status === COMMERCIAL_DATA_STATUS_SUCCESS"
    :is-error="serviceHasUnexpectedStatus(status)"
    :img="codatLogo"
    :date="codatDate"
    :json-data="jsonData"
    :provider-name="PROVIDER_CODAT"
    :service-name="$t('DEALS.CODAT.TITLE')"
    :service-type="$t('DEALS.CODAT.TITLE')"
  >
    <template #advanced>
      <div v-if="codatData" class="ml-2 flex">
        <lf-switch
          v-model="advancedMode"
          name="advanced-mode"
          class="ml-2"
          @toggle-changed="handleToggleChange"
        />
        <span class="mt-1 ml-2 text-xs font-semibold">
          {{ $t("DEALS.CODAT.ADVANCED_MODE") }}
        </span>
      </div>
    </template>
    <confirm-service-start
      :date="codatDate"
      :confirm-handler="startCodat"
      :status="status"
      :download-data="codatDataToDownload"
      :service-name="$t('DEALS.CODAT.TITLE')"
      service-type="$t('DEALS.CODAT.TITLE')"
      :is-loading="isLoading"
      :has-json-view="!isEmpty(jsonData)"
      :provider-name="PROVIDER_CODAT"
    >
      <template #download-options>
        <div class="grid w-max grid-cols-2 gap-2 p-4">
          <div v-for="item in codatData" :key="Number(item.id)" class="w-max">
            <lf-checkbox
              v-model="item.checked"
              :value="item.checked"
              :name="String(item.action)"
              class="w-full"
              @update:model-value="updateCodatData(Number(item.id), $event)"
            >
              {{ camelCaseToWords(String(item.action)) }}
            </lf-checkbox>
          </div>
        </div>
      </template>
    </confirm-service-start>
    <template #content>
      <div class="space-y-6">
        <div v-if="status === COMMERCIAL_DATA_STATUS_NOT_STARTED">
          <p>{{ $t("DEALS.DEAL_PROGRESS.UNDERWRITING.CODAT_DESCRIPTION") }}</p>
        </div>
        <service-error
          v-if="serviceHasUnexpectedStatus(status)"
          :have-response="!isEmpty(codatData)"
          :service-error-message="status"
        />
        <div v-if="!isEmpty(codatData)" class="space-y-5">
          <toggle-section
            chevron-position="right"
            section="attorneys"
            :is-open="isSectionOpen.companyInfo"
            :has-padding="false"
            @toggle="toggle('companyInfo')"
          >
            <template #titleSection>
              <div>
                <lf-h4>
                  {{ $t("DEALS.CODAT.COMPANY_INFO.TITLE") }}
                </lf-h4>
                <p class="text-sm font-normal text-label">
                  {{ $t("DEALS.CODAT.COMPANY_INFO.DESCRIPTION") }}
                </p>
              </div>
            </template>
            <template #content>
              <div
                v-for="(body, index) in companyInfo?.response_body"
                :key="index"
                class="p-4"
              >
                <div class="grid grid-cols-5 items-center">
                  <div class="flex">
                    <icon-base
                      :icon="IconCompany"
                      :width="48"
                      :height="48"
                      class="mr-4 place-self-center"
                    />
                    <div>
                      <lf-h4>{{ body?.companyLegalName || "-" }}</lf-h4>
                      <p class="text-sm font-normal text-label">
                        {{ body?.companyName || "-" }}
                      </p>
                    </div>
                  </div>
                  <div class="flex content-center">
                    <img
                      class="mr-4 h-13 w-13"
                      alt="accounting service"
                      src="/images/quickbooks.svg"
                      loading="lazy"
                    />
                    <div>
                      <lf-h4>
                        {{ companyInfo?.accounting_service_name || "-" }}
                      </lf-h4>
                      <p class="text-sm font-normal text-label">
                        {{ $t("DEALS.CODAT.ACCOUNTING_SERVICE") }}
                      </p>
                    </div>
                  </div>
                  <div
                    v-if="body?.baseCurrency"
                    class="flex content-center items-center"
                  >
                    <icon-base
                      :icon="CURRENCY_ICON[body?.baseCurrency]"
                      :width="28"
                      :height="15"
                      view-box="0 0 60 30"
                      class="mr-4 place-self-center"
                    />
                    <div>
                      <p class="text-sm font-normal text-headline">
                        {{ body?.baseCurrency || "-" }}
                      </p>
                    </div>
                  </div>
                  <div class="flex content-center items-center">
                    <icon-base
                      :icon="IconTelephone"
                      :width="28"
                      :height="20"
                      class="mr-4 place-self-center"
                      :icon-color="UTIL_COLORS.DEFAULT"
                    />
                    <div>
                      <p
                        v-for="(phoneNumber, index) in body?.phoneNumbers"
                        :key="index"
                        class="text-sm font-normal text-headline"
                      >
                        {{ phoneNumber?.number || "-" }}
                      </p>
                    </div>
                  </div>
                  <div class="flex content-center items-center">
                    <div class="h-5 w-5">
                      <icon-base
                        :icon="IconWebsite"
                        :width="28"
                        :height="20"
                        class="mr-4 place-self-center"
                      />
                    </div>
                    <div>
                      <p
                        v-for="(webLinks, index) in body?.webLinks"
                        :key="index"
                        class="text-sm font-normal break-all text-headline"
                      >
                        {{ webLinks?.url || "-" }}
                      </p>
                    </div>
                  </div>
                </div>
                <div class="mt-7 grid grid-cols-3 border-t border-b">
                  <div class="my-3 grid grid-cols-2">
                    <div class="py-4">
                      <p class="text-sm text-label">
                        {{
                          $t("DEALS.CODAT.COMPANY_INFO.REGISTRATION_NUMBER")
                        }}:
                      </p>
                      <p class="mt-3 text-sm text-label">
                        {{ $t("DEALS.CODAT.COMPANY_INFO.CREATED_DATE") }}:
                      </p>
                    </div>
                    <div class="py-4">
                      <p class="text-sm text-headline">
                        {{ body?.registrationNumber || "-" }}
                      </p>
                      <p class="mt-3 text-sm text-headline">
                        {{
                          formatDateCustom(body?.createdDate, "MMM d, yyyy ") ||
                          "-"
                        }}
                      </p>
                    </div>
                  </div>
                  <div class="my-3 grid grid-cols-2 border-r border-l">
                    <div class="px-3 py-4">
                      <p class="text-sm text-label">
                        {{ $t("DEALS.CODAT.COMPANY_INFO.TAX_NUMBER") }}:
                      </p>
                      <p class="mt-3 text-sm text-label">
                        {{ $t("DEALS.CODAT.COMPANY_INFO.LEDGER_LOCK_DATE") }}:
                      </p>
                    </div>
                    <div class="py-4">
                      <p class="text-sm break-all text-headline">
                        {{ body?.taxNumber || "-" }}
                      </p>
                      <p class="mt-3 text-sm text-headline">-</p>
                    </div>
                  </div>
                  <div class="my-3 grid grid-cols-2">
                    <div class="px-3 py-4">
                      <p class="text-sm text-label">
                        {{
                          $t("DEALS.CODAT.COMPANY_INFO.FINANCIAL_YET_STARTED")
                        }}
                      </p>
                    </div>
                    <div class="py-4">
                      <p class="text-sm text-headline">
                        {{
                          formatDateCustom(
                            body?.financialYearStartDate,
                            "MMM, d, yyyy"
                          ) || "-"
                        }}
                      </p>
                    </div>
                  </div>
                </div>
                <div class="mt-7">
                  <div>
                    <p class="mb-4 text-xxs font-bold text-label uppercase">
                      {{ $t("DEALS.CODAT.COMPANY_INFO.ADDRESSES") }}
                    </p>
                    <div
                      v-for="(address, index) in body?.addresses || []"
                      :key="index"
                      class="mb-4 flex justify-between"
                    >
                      <p class="text-sm text-label">{{ address?.type }}:</p>
                      <p class="text-sm text-headline">
                        {{ address?.line1 }}, {{ address?.line2 }} ,
                        {{ address?.country }} {{ address?.postalCode }}
                      </p>
                    </div>
                  </div>
                  <div class="mt-4">
                    <p class="mb-4 text-xxs font-bold text-label uppercase">
                      {{ $t("DEALS.CODAT.COMPANY_INFO.PHONES") }}:
                    </p>
                    <div
                      v-for="(phone, index) in body?.phoneNumbers || []"
                      :key="index"
                      class="mb-4 flex justify-between"
                    >
                      <p class="text-sm text-label">{{ phone?.type }}:</p>
                      <p class="text-sm text-headline">
                        {{ phone?.number }}
                      </p>
                    </div>
                  </div>
                  <div class="mt-4">
                    <p class="mb-4 text-xxs font-bold text-label uppercase">
                      {{ $t("DEALS.CODAT.COMPANY_INFO.WEB_LINKS") }}
                    </p>
                    <div
                      v-for="(website, index) in body?.webLinks || []"
                      :key="index"
                      class="mb-4 flex justify-between"
                    >
                      <p class="text-sm text-label">{{ website?.type }}:</p>
                      <p class="text-sm text-headline">
                        {{ website?.url }}
                      </p>
                    </div>
                  </div>
                  <div class="mt-4">
                    <p class="mb-4 text-xxs font-bold text-label uppercase">
                      {{ $t("DEALS.CODAT.COMPANY_INFO.SOURCE_LINKS") }}
                    </p>
                    <div
                      v-for="(source, index) in body?.sourceUrls || []"
                      :key="index"
                      class="mb-4 flex justify-between"
                    >
                      <p class="text-sm text-label"></p>
                      <p class="text-sm text-headline">
                        {{ source }}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </toggle-section>
          <toggle-section
            chevron-position="right"
            section="attorneys"
            :is-open="isSectionOpen.profitAndLoss"
            :has-padding="false"
            @toggle="toggle('profitAndLoss')"
          >
            <template #titleSection>
              <div>
                <lf-h4>
                  {{ $t("DEALS.CODAT.PROFIT_AND_LOSS.TITLE") }}
                </lf-h4>
                <p class="text-sm font-normal text-label">
                  {{ $t("DEALS.CODAT.PROFIT_AND_LOSS.DESCRIPTION") }}
                </p>
              </div>
            </template>
            <template #content>
              <report-section
                :data-filter="profitDataFilter"
                :chart-filter="chartProfitFilter"
                :data="profitAndLossData"
                :legend="profitAndLossLegend"
              />
            </template>
          </toggle-section>
          <toggle-section
            chevron-position="right"
            section="balanceSheets"
            :is-open="isSectionOpen.balanceSheets"
            :has-padding="false"
            @toggle="toggle('balanceSheets')"
          >
            <template #titleSection>
              <div>
                <lf-h4>
                  {{ $t("DEALS.CODAT.BALANCE_SHEET.TITLE") }}
                </lf-h4>
                <p class="text-sm font-normal text-label">
                  {{ $t("DEALS.CODAT.BALANCE_SHEET.DESCRIPTION") }}
                </p>
              </div>
            </template>
            <template #content>
              <report-section
                :data="balanceSheets"
                :data-filter="balanceDataFilter"
                :chart-filter="balanceDataFilter"
                :legend="balanceLegend"
              />
            </template>
          </toggle-section>
          <toggle-section
            v-if="advancedMode"
            chevron-position="right"
            section="accounts"
            :is-open="isSectionOpen.accounts"
            :has-padding="false"
            @toggle="toggle('accounts')"
          >
            <template #titleSection>
              <div class="flex w-full items-center justify-between">
                <div>
                  <lf-h4>
                    {{ $t("DEALS.CODAT.ACCOUNTS") }}
                  </lf-h4>
                  <p class="text-sm font-normal text-label">
                    {{ $t("DEALS.CODAT.ACCOUNTS_DESCRIPTION") }}
                  </p>
                </div>
                <lf-pill class="pill mr-5" :color-index="15">
                  {{ accounts?.length || 0 }} {{ $t("DEALS.CODAT.ACCOUNTS") }}
                </lf-pill>
              </div>
            </template>
            <template #content>
              <table-slots :data="accounts" :header-fields="accountFields">
                <template #cell(status)="{ value }">
                  <lf-pill
                    :color-index="
                      typeof value === 'string' && value === 'Active' ? 0 : 26
                    "
                  >
                    {{ value }}
                  </lf-pill>
                </template>
                <template #cell(type)="{ value }">
                  <lf-pill :color-index="15">
                    {{ value }}
                  </lf-pill>
                </template>
                <template #cell(isBankAccount)="{ value }">
                  <icon-base
                    v-if="value"
                    class="mr-1 text-checkmark-green"
                    :icon="IconSuccessRoundBig"
                  />
                  <icon-base v-else :icon="IconFailBigRed" />
                </template>
                <template #cell(sourceModifiedDate)="{ value }">
                  {{ formatDateCustom(value?.toString(), "MMM d, yyyy") }}
                </template>
              </table-slots>
            </template>
          </toggle-section>
          <toggle-section
            v-if="advancedMode"
            chevron-position="right"
            section="taxRates"
            :is-open="isSectionOpen.taxRates"
            :has-padding="false"
            @toggle="toggle('taxRates')"
          >
            <template #titleSection>
              <div class="flex w-full items-center justify-between">
                <div>
                  <lf-h4>
                    {{ $t("DEALS.CODAT.TAX_RATES") }}
                  </lf-h4>
                  <p class="w-full text-sm font-normal text-label">
                    {{ $t("DEALS.CODAT.TAX_RATES_DESCRIPTION") }}
                  </p>
                </div>
                <lf-pill class="pill mr-5" :color-index="15">
                  {{ taxRates?.length || 0 }} {{ $t("DEALS.CODAT.PRESETS") }}
                </lf-pill>
              </div>
            </template>
            <template #content>
              <table-slots
                :data="taxRates"
                :header-fields="taxRatesFields"
                :toggle-field="taxCompoundFields"
                parameter-open-toggle="components"
                has-toggle-box
              >
                <template #cell(sourceModifiedDate)="{ value }">
                  {{ formatDateCustom(value?.toString(), "MMM d, yyyy") }}
                </template>
                <template #cell(components)="{ value }">
                  {{ value?.length || "-" }}
                </template>
                <template #cell(effectiveTaxRate)="{ value }">
                  {{ value }}%
                </template>
                <template #cell(totalTaxRate)="{ value }">
                  {{ value }}%
                </template>
                <template #box(name)="{ item }">
                  {{ compoundText(item) }}
                </template>
                <template #box(rate)="{ value }">{{ value }}%</template>
                <template #box(isCompound)="{ value }">
                  <p :class="value ? 'text-success' : 'text-error'">
                    {{ value ? $t("COMMON.YES") : $t("COMMON.NO") }}
                  </p>
                </template>
              </table-slots>
            </template>
          </toggle-section>
        </div>
        <div
          v-else-if="
            !serviceHasUnexpectedStatus(status) &&
            status !== COMMERCIAL_DATA_STATUS_NOT_STARTED
          "
        >
          {{ status }}
        </div>
      </div>
    </template>
    <widget-link :service="PROVIDER_CODAT" />
  </service-wrapper>
</template>
<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import isEmpty from "lodash/isEmpty";

import { convertToArray } from "@/helpers/common";
import {
  COMMERCIAL_DATA_STATUS_NOT_STARTED,
  COMMERCIAL_DATA_STATUS_SUCCESS,
  CURRENCY_ICON,
  PROVIDER_CODAT,
  UTIL_COLORS,
} from "@/helpers/constants";
import { camelCaseToWords } from "@/helpers/formatting";
import { formatDateCustom } from "@/helpers/formatting";
import {
  getImageForService,
  serviceHasUnexpectedStatus,
} from "@/helpers/services";
import { useDealsBase } from "@/hooks/deals";

import ToggleSection from "@/components/ToggleSection.vue";
import WidgetLink from "@/components/WidgetLink.vue";
import ReportSection from "@/components/codat/ReportSection.vue";
import TableSlots from "@/components/codat/TableSlots.vue";
import ServiceError from "@/components/deals/dataServices/ServiceError.vue";
import IconCompany from "@/components/icons/IconCompany.vue";
import IconFailBigRed from "@/components/icons/IconFailBigRed.vue";
import IconSuccessRoundBig from "@/components/icons/IconSuccessRoundBig.vue";
import IconTelephone from "@/components/icons/IconTelephone.vue";
import IconWebsite from "@/components/icons/IconWebsite.vue";
import LfCheckbox from "@/components/ui/inputs/LfCheckbox.vue";
import ConfirmServiceStart from "@/views/businessCreditDeals/components/ConfirmServiceStart.vue";

import type { Codat } from "@/models/commercialData/Codat";

const { t } = useI18n();
const { dispatch } = useStore();
const { activeDeal } = useDealsBase();

const codatLogo = getImageForService(16) ?? undefined;

const isLoading = ref(false);
const isSectionOpen = ref({
  companyInfo: false,
  profitAndLoss: false,
  balanceSheets: false,
  taxRates: false,
  accounts: false,
});
const advancedMode = ref(true);
const chartProfitFilter = ref([
  "Income",
  "Cost of Sales",
  "grossProfit",
  "Expenses",
  "netOperatingProfit",
]);

const profitDataFilter = ref([
  "netProfit",
  "netOperatingProfit",
  "Income",
  "Expenses",
]);
const balanceDataFilter = ref(["Assets", "Equity", "netAssets", "Liabilities"]);

const profitAndLossLegend = [
  {
    label: t("DEALS.CODAT.PROFIT_AND_LOSS.INCOME"),
    color: UTIL_COLORS.SUCCESS,
  },
  {
    label: t("DEALS.CODAT.PROFIT_AND_LOSS.COST_OF_SALES"),
    color: UTIL_COLORS.PRIMARY,
  },
  {
    label: t("DEALS.CODAT.PROFIT_AND_LOSS.GROSS_PROFIT"),
    color: UTIL_COLORS.PURPLE,
  },
  {
    label: t("DEALS.CODAT.PROFIT_AND_LOSS.EXPENSES"),
    color: UTIL_COLORS.ERROR,
  },
  {
    label: t("DEALS.CODAT.PROFIT_AND_LOSS.NET_OPERATING_PROFIT"),
    color: UTIL_COLORS.WARNING,
  },
];
const balanceLegend = [
  {
    label: t("DEALS.CODAT.BALANCE_SHEET.ASSETS"),
    color: UTIL_COLORS.SUCCESS,
  },
  {
    label: t("DEALS.CODAT.BALANCE_SHEET.LIABILITIES"),
    color: UTIL_COLORS.PRIMARY,
  },
  {
    label: t("DEALS.CODAT.BALANCE_SHEET.EQUITY"),
    color: UTIL_COLORS.PURPLE,
  },
  {
    label: t("DEALS.CODAT.BALANCE_SHEET.NET_ASSETS"),
    color: UTIL_COLORS.ERROR,
  },
];

const jsonData = computed(
  () => activeDeal.value?.commercialData?.codat || null
);

const codatData = computed(
  () =>
    Object.values(activeDeal.value?.commercialData?.codat || {})?.map(
      (item) => ({
        ...item,
        checked: true,
      })
    ) as (Codat & { checked: boolean })[]
);
const profitAndLossData = computed(
  () => codatData.value.find((data) => data.action === "profitAndLoss") as Codat
);
const balanceSheets = computed(
  () => codatData.value.find((data) => data.action === "balanceSheets") as Codat
);
const companyInfo = computed(
  () => codatData.value.find((data) => data.action === "companyInfo") as Codat
);
const accounts = computed(() => {
  const accountsData = codatData.value.find(
    (data) => data.action === "accounts"
  )?.response_body?.[1];

  return convertToArray(accountsData);
});
const taxRates = computed(() => {
  const taxRatesData = codatData.value.find(
    (data) => data.action === "taxRates"
  )?.response_body?.[1];

  return convertToArray(taxRatesData);
});

const codatDataToDownload = ref(codatData.value);

const status = computed(
  () => activeDeal.value?.commercialData?.statuses?.codat
);

const codatDate = computed<string>(
  () => activeDeal.value?.commercialData?.dates?.codat || ""
);

const accountFields = ref([
  { key: "name", label: t("DEALS.CODAT.TABLE_LABEL.NAME") },
  { key: "status", label: t("DEALS.CODAT.TABLE_LABEL.STATUS") },
  { key: "type", label: t("DEALS.CODAT.TABLE_LABEL.TYPE") },
  { key: "currency", label: t("DEALS.CODAT.TABLE_LABEL.CURRENCY") },
  { key: "isBankAccount", label: t("DEALS.CODAT.TABLE_LABEL.BANK_ACCOUNT") },
  { key: "description", label: t("DEALS.CODAT.TABLE_LABEL.DESCRIPTION") },
  { key: "nominalCode", label: t("DEALS.CODAT.TABLE_LABEL.CODE") },
  {
    key: "sourceModifiedDate",
    label: t("DEALS.CODAT.TABLE_LABEL.UPTADED_SOURCE"),
  },
]);

const taxRatesFields = ref([
  { key: "name", label: t("DEALS.CODAT.TABLE_LABEL.NAME") },
  { key: "code", label: t("DEALS.CODAT.TABLE_LABEL.CODE") },
  {
    key: "effectiveTaxRate",
    label: t("DEALS.CODAT.TABLE_LABEL.EFFECTIVE_TAX_RATE"),
  },
  { key: "totalTaxRate", label: t("DEALS.CODAT.TABLE_LABEL.TOTAL_TAX_RATE") },
  { key: "components", label: t("DEALS.CODAT.TABLE_LABEL.COMPONENTS") },
  {
    key: "sourceModifiedDate",
    label: t("DEALS.CODAT.TABLE_LABEL.UPTADED_SOURCE"),
  },
]);

const taxCompoundFields = ref([
  { key: "name", label: t("DEALS.CODAT.TABLE_LABEL.TAX_COMPONENT_NAME") },
  { key: "rate", label: t("DEALS.CODAT.TABLE_LABEL.RATE") },
  {
    key: "isCompound",
    label: t("DEALS.CODAT.TABLE_LABEL.COMPOUND"),
  },
]);

const updateCodatData = (id: number, val: boolean) => {
  const data = codatData.value?.filter((item) => {
    if (item.id === id) {
      item.checked = val;
    }
    return item.checked;
  });
  codatDataToDownload.value = data?.length ? data : [];
};

const handleToggleChange = () => {
  advancedMode.value = !advancedMode.value;
};

watch(
  codatData,
  () => {
    codatDataToDownload.value = codatData.value;
  },
  { immediate: true }
);

const compoundText = (component: Record<string, string>) => {
  return `${component.isCompound ? "Compound" : "Non-compound"} ${
    component.name
  }`;
};

const startCodat = async () => {
  isLoading.value = true;
  try {
    await dispatch("applications/enrichApp", {
      provider: PROVIDER_CODAT,
    });
  } finally {
    isLoading.value = false;
  }
};

const toggle = (section: keyof typeof isSectionOpen.value) => {
  isSectionOpen.value[section] = !isSectionOpen.value[section];
};
</script>
<style scoped>
@reference "@/assets/tailwind.css";

.line {
  height: 1px;
  width: 200%;
  position: absolute;
  left: -20%;
  margin-top: 10px;
}

.pill {
  height: fit-content;
  width: 110px;
}
</style>
